import { cloneElement, Fragment } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import classNames from 'classnames';
import CIcon from '@coreui/icons-react';
import { cilBan } from '@coreui/icons';
import { cellText, rowClasses } from './DataTable.utils';

/** A column: the item property it shows and its header text. */
export interface IDataTableField {
  key: string;
  label: string;
}

interface IDataTableProps<TItem extends object> {
  items: TItem[];
  fields: IDataTableField[];
  /** Renders the `<td>` of a column instead of the plain value. */
  scopedSlots?: Partial<Record<string, (item: TItem, index: number) => ReactElement>>;
  /** Shown in the single row of an empty table instead of the "No items" text. */
  noItemsViewSlot?: ReactNode;
  /** Pointer cursor and focusable rows. */
  clickableRows?: boolean;
  onRowClick?: (item: TItem, index: number) => unknown;
  hover?: boolean;
  size?: 'sm';
  /** Classes added to the `<table>`. */
  addTableClasses?: string;
}

const HEADER_STYLE: CSSProperties = { verticalAlign: 'middle', overflow: 'hidden' };

const NoItems = () => (
  <div className="text-center my-5">
    <h2>
      {'No items '}
      <CIcon width={30} icon={cilBan} className="text-danger mb-2" />
    </h2>
  </div>
);

/**
 * The part of CoreUI 3's CDataTable the lists use, with its markup: all items on one page (the lists paginate on the
 * server), no sorting and no filters.
 */
const DataTable = <TItem extends object>({
  items,
  fields,
  scopedSlots = {},
  noItemsViewSlot,
  clickableRows,
  onRowClick,
  hover,
  size,
  addTableClasses,
}: IDataTableProps<TItem>) => {
  const tableClasses = classNames(
    'table',
    { [`table-${size}`]: size, 'table-hover': hover },
    addTableClasses,
  );

  return (
    <>
      <div />
      <div className="position-relative table-responsive">
        <table className={tableClasses}>
          <thead>
            <tr>
              {fields.map(({ key, label }) => (
                <th className="" style={HEADER_STYLE} key={key}>
                  <div className="d-inline">{label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody style={clickableRows ? { cursor: 'pointer' } : undefined}>
            {items.map((item, itemIndex) => (
              // eslint-disable-next-line react/no-array-index-key -- rows are kept by position, as in CDataTable
              <Fragment key={itemIndex}>
                <tr
                  className={rowClasses(item)}
                  tabIndex={clickableRows ? 0 : undefined}
                  onClick={() => onRowClick?.(item, itemIndex)}
                >
                  {fields.map(({ key }) => {
                    const slot = scopedSlots[key];
                    if (slot) return cloneElement(slot(item, itemIndex), { key });
                    return (
                      <td className="" key={key}>
                        {cellText(item, key)}
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={fields.length}>{noItemsViewSlot || <NoItems />}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DataTable;
